import { createContext, useContext, useEffect, useRef } from 'react';

/**
 * The realtime context and its hooks, apart from the provider that fills them (Phase 103).
 *
 * React Fast Refresh updates a module in place only when everything it exports is a component. A
 * provider module that also exports hooks is invalidated on every edit — and a reloaded module
 * means a new context object while the mounted tree still holds the old one.
 *
 * Here that failure is quieter than the auth one and worse for it: `useRealtime` deliberately falls
 * back to a dead connection rather than throwing, so a stale context does not crash anything. It
 * just stops delivering, and a screen that is silently not live looks exactly like a quiet
 * afternoon.
 */

export interface RealtimeEvent {
  type: string;
  topic: string;
  payload: unknown;
  at?: string;
}

export type Handler = (event: RealtimeEvent) => void;

export interface RealtimeValue {
  /** Whether the connection is currently up. Screens may show it; none may depend on it. */
  connected: boolean;
  /** Who is online in this tenant, by user id. */
  online: ReadonlySet<string>;
  subscribe: (topic: string, handler: Handler) => () => void;
  /** Called after every reconnection, so a screen can reconcile what it missed. */
  onReconnect: (handler: () => void) => () => void;
}

export const RealtimeContext = createContext<RealtimeValue | undefined>(undefined);

/**
 * The provider, or a dead one.
 *
 * Returning a no-op rather than throwing: a screen rendered outside the provider — a public page, a
 * test — should render without live updates rather than crash. Live updates are an improvement on
 * the screen, never the thing that makes it work.
 */
export function useRealtime(): RealtimeValue {
  return (
    useContext(RealtimeContext) ?? {
      connected: false,
      online: new Set<string>(),
      subscribe: () => () => undefined,
      onReconnect: () => () => undefined,
    }
  );
}

/**
 * Listens to one topic while the component is mounted.
 *
 * `null` unsubscribes, which is what a screen with nothing selected wants. The handler is kept in a
 * ref so a component that re-renders on every event does not re-subscribe on every render.
 */
export function useRealtimeTopic(topic: string | null, handler: Handler): void {
  const { subscribe } = useRealtime();
  const latest = useRef(handler);

  latest.current = handler;

  useEffect(() => {
    if (!topic) return;

    return subscribe(topic, (event) => latest.current(event));
  }, [topic, subscribe]);
}

/** Runs after every reconnection, so a screen can refetch what it missed while it was down. */
export function useRealtimeReconnect(handler: () => void): void {
  const { onReconnect } = useRealtime();
  const latest = useRef(handler);

  latest.current = handler;

  useEffect(() => onReconnect(() => latest.current()), [onReconnect]);
}
